/**
 * Expense Reports State Management Store
 *
 * Handles expense report data, filtering, and API interactions
 * using pub/sub pattern consistent with requests-store.ts
 */

import { apiClient } from './api-client';
import type {
  ExpenseReportSummary,
  ExpenseReportDetail,
  ExpenseReportStatus,
  ExpenseReportListResponse,
  ExpenseReportResponse,
  Receipt,
  ReceiptResponse,
  ExpenseType,
} from '../types/expense-tracker';

/**
 * Expense reports store state shape
 */
export interface ExpenseReportsState {
  /** All loaded expense reports */
  reports: ExpenseReportSummary[];
  /** Currently selected report (for detail view) */
  selectedReport: ExpenseReportDetail | null;
  /** Loading state for list */
  loading: boolean;
  /** Loading state for detail */
  loadingDetail: boolean;
  /** Error message */
  error: string | null;
  /** Current status filter */
  statusFilter: ExpenseReportStatus | null;
}

/**
 * Subscriber callback type
 */
type Subscriber = (state: ExpenseReportsState) => void;

/**
 * Initial state
 */
const initialState: ExpenseReportsState = {
  reports: [],
  selectedReport: null,
  loading: false,
  loadingDetail: false,
  error: null,
  statusFilter: null,
};

/**
 * Expense Reports Store class
 */
class ExpenseReportsStore {
  private state: ExpenseReportsState = { ...initialState };
  private subscribers: Set<Subscriber> = new Set();

  /**
   * Subscribe to state changes
   * Returns unsubscribe function
   */
  subscribe(callback: Subscriber): () => void {
    this.subscribers.add(callback);
    // Immediately call with current state
    callback(this.state);
    return () => this.subscribers.delete(callback);
  }

  /**
   * Get current state
   */
  getState(): ExpenseReportsState {
    return this.state;
  }

  /**
   * Notify all subscribers of state change
   */
  private notify(): void {
    this.subscribers.forEach((cb) => cb(this.state));
  }

  /**
   * Update state and notify subscribers
   */
  private setState(partial: Partial<ExpenseReportsState>): void {
    this.state = { ...this.state, ...partial };
    this.notify();
  }

  /**
   * Fetch all expense reports for the current user
   */
  async fetchReports(status?: ExpenseReportStatus): Promise<void> {
    this.setState({ loading: true, error: null, statusFilter: status ?? null });

    try {
      const params: Record<string, string> = {};
      if (status) {
        params.status = status;
      }

      const response = await apiClient.get<ExpenseReportListResponse>('/expense-reports', { params });
      const reports = response.data?.data ?? [];

      this.setState({ reports, loading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch expense reports';
      this.setState({ error: message, loading: false });
    }
  }

  /**
   * Fetch a single expense report with receipts
   */
  async fetchReport(reportId: string): Promise<ExpenseReportDetail | null> {
    this.setState({ loadingDetail: true, error: null });

    try {
      const response = await apiClient.get<ExpenseReportResponse>(`/expense-reports/${reportId}`);
      const report = response.data?.data ?? null;

      this.setState({ selectedReport: report, loadingDetail: false });
      return report;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch expense report';
      this.setState({ error: message, loadingDetail: false, selectedReport: null });
      return null;
    }
  }

  /**
   * Create a new expense report linked to an expenditure request
   */
  async createReport(expenditureRequestId: string): Promise<ExpenseReportDetail | null> {
    this.setState({ loading: true, error: null });

    try {
      const response = await apiClient.post<ExpenseReportResponse>('/expense-reports', {
        expenditureRequestId,
      });
      const report = response.data?.data ?? null;

      // Refresh the list
      await this.fetchReports(this.state.statusFilter ?? undefined);

      return report;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create expense report';
      this.setState({ error: message, loading: false });
      throw error;
    }
  }

  /**
   * Delete an expense report
   */
  async deleteReport(reportId: string): Promise<void> {
    this.setState({ loading: true, error: null });

    try {
      await apiClient.delete(`/expense-reports/${reportId}`);

      // Remove from local state
      const reports = this.state.reports.filter((r) => r.id !== reportId);
      this.setState({
        reports,
        loading: false,
        selectedReport: this.state.selectedReport?.id === reportId ? null : this.state.selectedReport,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete expense report';
      this.setState({ error: message, loading: false });
      throw error;
    }
  }

  /**
   * Upload a receipt to an expense report and trigger AI extraction
   */
  async uploadReceipt(reportId: string, file: File): Promise<Receipt | null> {
    try {
      const formData = new FormData();
      formData.append('receipt', file);

      const response = await apiClient.post<ReceiptResponse>(
        `/expense-reports/${reportId}/receipts`,
        formData
      );

      const receipt = response.data?.data ?? null;

      // Trigger AI extraction if upload succeeded
      if (receipt) {
        try {
          await apiClient.post(`/receipts/${receipt.id}/extract`);
        } catch (extractError) {
          // Extraction failure is non-fatal - user can enter data manually
          console.warn('AI extraction failed, manual entry required:', extractError);
        }
      }

      // Refresh the current report to get updated receipt list with extraction data
      if (this.state.selectedReport?.id === reportId) {
        await this.fetchReport(reportId);
      }

      return receipt;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to upload receipt';
      this.setState({ error: message });
      throw error;
    }
  }

  /**
   * Delete a receipt from an expense report
   */
  async deleteReceipt(receiptId: string): Promise<void> {
    // Store original state for rollback
    const originalReport = this.state.selectedReport;

    // Optimistic update: immediately remove receipt from local state
    if (this.state.selectedReport) {
      const updatedReceipts = this.state.selectedReport.receipts.filter(r => r.id !== receiptId);

      // Recalculate totals optimistically
      const newTotal = this.calculateLocalTotal(updatedReceipts);
      const budgetSummary = this.recalculateLocalBudget(this.state.selectedReport, newTotal, updatedReceipts);

      this.setState({
        selectedReport: {
          ...this.state.selectedReport,
          receipts: updatedReceipts,
          total_amount_chf: newTotal,
          ...(budgetSummary && { budgetSummary })
        },
      });
    }

    try {
      await apiClient.delete(`/receipts/${receiptId}`);

      // Refresh the current report to get accurate server data
      if (this.state.selectedReport) {
        await this.fetchReport(this.state.selectedReport.id);
      }
    } catch (error) {
      // Rollback on error
      if (originalReport) {
        this.setState({ selectedReport: originalReport });
      }

      const message = error instanceof Error ? error.message : 'Failed to delete receipt';
      this.setState({ error: message });
      throw error;
    }
  }

  /**
   * Update receipt data with optimistic UI updates
   */
  async updateReceipt(receiptId: string, data: {
    vendor?: string;
    receipt_date?: string;
    amount?: number;
    currency?: string;
    vat_percent?: number;
    expense_type?: ExpenseType;
    notes?: string;
  }): Promise<Receipt | null> {
    // Store original state for rollback
    const originalReport = this.state.selectedReport;
    const originalReceipt = originalReport?.receipts.find(r => r.id === receiptId);

    // Optimistic update: immediately update local state
    if (this.state.selectedReport && originalReceipt) {
      const optimisticReceipt = { ...originalReceipt, ...data };
      const updatedReceipts = this.state.selectedReport.receipts.map((r) =>
        r.id === receiptId ? optimisticReceipt : r
      );

      // Recalculate totals optimistically
      const newTotal = this.calculateLocalTotal(updatedReceipts);
      const budgetSummary = this.recalculateLocalBudget(this.state.selectedReport, newTotal, updatedReceipts);

      this.setState({
        selectedReport: {
          ...this.state.selectedReport,
          receipts: updatedReceipts,
          total_amount_chf: newTotal,
          ...(budgetSummary && { budgetSummary })
        },
      });
    }

    try {
      const response = await apiClient.patch<ReceiptResponse>(`/receipts/${receiptId}`, data);
      const receipt = response.data?.data ?? null;

      // Update with actual server response
      if (receipt && this.state.selectedReport) {
        // Fetch fresh report data for accurate budget info
        await this.fetchReport(this.state.selectedReport.id);
      }

      return receipt;
    } catch (error) {
      // Rollback on error
      if (originalReport) {
        this.setState({ selectedReport: originalReport });
      }

      const message = error instanceof Error ? error.message : 'Failed to update receipt';
      this.setState({ error: message });
      throw error;
    }
  }

  /**
   * Calculate total from receipts locally
   */
  private calculateLocalTotal(receipts: Receipt[]): number {
    return receipts.reduce((sum, r) => sum + (r.amount_chf ?? 0), 0);
  }

  /**
   * Recalculate budget summary locally
   */
  private recalculateLocalBudget(
    report: ExpenseReportDetail,
    newTotal: number,
    receipts: Receipt[]
  ): any | null {
    if (!report.expenditure_request) return null;

    const approvedBudgetChf = report.expenditure_request.amount; // Assume CHF for simplicity
    const remaining = approvedBudgetChf - newTotal;
    const percentUsed = approvedBudgetChf > 0
      ? Math.round((newTotal / approvedBudgetChf) * 100)
      : 0;

    return {
      approvedBudget: report.expenditure_request.amount,
      approvedBudgetChf,
      totalSpent: newTotal,
      remaining,
      percentUsed,
      isOverBudget: newTotal > approvedBudgetChf,
      receiptCount: receipts.length
    };
  }

  /**
   * Recalculate totals for an expense report
   */
  async recalculateTotals(reportId: string): Promise<void> {
    try {
      await apiClient.post<ExpenseReportResponse>(`/expense-reports/${reportId}/recalculate`);

      // Refresh the current report
      if (this.state.selectedReport?.id === reportId) {
        await this.fetchReport(reportId);
      }

      // Refresh the list to update totals
      await this.fetchReports(this.state.statusFilter ?? undefined);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to recalculate totals';
      this.setState({ error: message });
      throw error;
    }
  }

  /**
   * Update expense report status
   */
  async updateStatus(reportId: string, status: ExpenseReportStatus): Promise<ExpenseReportDetail | null> {
    try {
      const response = await apiClient.patch<ExpenseReportResponse>(`/expense-reports/${reportId}`, {
        status,
      });

      const report = response.data?.data ?? null;

      // Update local state
      if (report) {
        const reports = this.state.reports.map((r) =>
          r.id === reportId ? { ...r, status: report.status } : r
        );
        this.setState({
          reports,
          selectedReport: this.state.selectedReport?.id === reportId ? report : this.state.selectedReport,
        });
      }

      return report;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update status';
      this.setState({ error: message });
      throw error;
    }
  }

  /**
   * Set status filter and refetch
   */
  async setStatusFilter(status: ExpenseReportStatus | null): Promise<void> {
    await this.fetchReports(status ?? undefined);
  }

  /**
   * Clear the selected report
   */
  clearSelectedReport(): void {
    this.setState({ selectedReport: null });
  }

  /**
   * Clear error
   */
  clearError(): void {
    this.setState({ error: null });
  }

  /**
   * Reset store to initial state
   */
  reset(): void {
    this.state = { ...initialState };
    this.notify();
  }

  /**
   * Mark as paid for an expense report (Team Assistant action)
   * Transitions status from SUBMITTED to PAID (simplified workflow)
   */
  async markAsPaid(reportId: string): Promise<ExpenseReportDetail | null> {
    this.setState({ loadingDetail: true, error: null });

    try {
      // First, trigger the status update
      await apiClient.post<ExpenseReportResponse>(
        `/expense-reports/${reportId}/paid`
      );

      // Then fetch the full report details to get receipts, status_history, etc.
      const fullReport = await this.fetchReport(reportId);

      // Update list view status
      const reports = this.state.reports.map((r) =>
        r.id === reportId ? { ...r, status: 'PAID' as const } : r
      );
      this.setState({ reports, loadingDetail: false });

      return fullReport;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to mark as paid';
      this.setState({ error: message, loadingDetail: false });
      throw error;
    }
  }

  /**
   * Revert payment status to SUBMITTED (Team Assistant error correction)
   * Transitions status from PAID back to SUBMITTED
   */
  async revertToSubmitted(reportId: string, reason?: string): Promise<ExpenseReportDetail | null> {
    this.setState({ loadingDetail: true, error: null });

    try {
      // First, trigger the status revert
      await apiClient.post<ExpenseReportResponse>(
        `/expense-reports/${reportId}/revert-status`,
        { reason }
      );

      // Then fetch the full report details to get receipts, status_history, etc.
      const fullReport = await this.fetchReport(reportId);

      // Update list view status
      const reports = this.state.reports.map((r) =>
        r.id === reportId ? { ...r, status: 'SUBMITTED' as const } : r
      );
      this.setState({ reports, loadingDetail: false });

      return fullReport;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to revert status';
      this.setState({ error: message, loadingDetail: false });
      throw error;
    }
  }
}

// Export singleton instance
export const expenseReportsStore = new ExpenseReportsStore();

export default expenseReportsStore;
